/**
 * Metric Quality Assessment Utilities
 *
 * Provides functions to interpret model metrics in plain language
 * and determine visual quality indicators for non-statisticians.
 */

export type QualityLevel = 'excellent' | 'good' | 'fair' | 'poor';

export interface MetricQuality {
  level: QualityLevel;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  percentage: number; // 0-100 for progress bar
}

/**
 * Assess NRMSE (Normalized Root Mean Square Error) quality
 * NRMSE measures prediction error as a percentage of the data range.
 * Lower is better: 0% = perfect predictions
 *
 * Thresholds based on marketing mix modeling best practices:
 * - Excellent: < 10% (very accurate predictions)
 * - Good: 10-15% (reliable predictions)
 * - Fair: 15-25% (acceptable but room for improvement)
 * - Poor: > 25% (model needs refinement)
 */
export function assessNRMSE(nrmse: number | undefined): MetricQuality {
  if (nrmse === undefined || nrmse === null) {
    return {
      level: 'fair',
      label: 'Unknown',
      description: 'NRMSE value not available',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-300',
      percentage: 50,
    };
  }

  const pct = nrmse * 100; // Convert to percentage

  if (pct < 10) {
    return {
      level: 'excellent',
      label: 'Excellent',
      description: 'Your model predictions are highly accurate. The model captures the patterns in your data very well.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-400',
      percentage: Math.max(0, 100 - pct * 4), // 0% NRMSE = 100%, 25% NRMSE = 0%
    };
  } else if (pct < 15) {
    return {
      level: 'good',
      label: 'Good',
      description: 'Your model predictions are reliable. This is a solid result for marketing mix modeling.',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
      percentage: Math.max(0, 100 - pct * 4),
    };
  } else if (pct < 25) {
    return {
      level: 'fair',
      label: 'Fair',
      description: 'Your model is working but has room for improvement. Consider adding more data or adjusting channel configurations.',
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-400',
      percentage: Math.max(0, 100 - pct * 4),
    };
  } else {
    return {
      level: 'poor',
      label: 'Needs Work',
      description: 'The model is struggling to make accurate predictions. Review your data quality and channel settings.',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
      percentage: Math.max(0, 100 - pct * 4),
    };
  }
}

/**
 * Assess R² (R-squared / Coefficient of Determination) quality
 * R² measures what percentage of variance in your outcome the model explains.
 * Higher is better: 1.0 = explains all variance, 0 = explains nothing
 *
 * Thresholds for marketing mix models:
 * - Excellent: > 0.85 (explains 85%+ of variance)
 * - Good: 0.70-0.85 (solid explanatory power)
 * - Fair: 0.50-0.70 (moderate explanatory power)
 * - Poor: < 0.50 (weak explanatory power)
 */
export function assessR2(r2: number | undefined): MetricQuality {
  if (r2 === undefined || r2 === null) {
    return {
      level: 'fair',
      label: 'Unknown',
      description: 'R² value not available',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-300',
      percentage: 50,
    };
  }

  if (r2 > 0.85) {
    return {
      level: 'excellent',
      label: 'Excellent',
      description: `Your model explains ${(r2 * 100).toFixed(0)}% of revenue changes. This is a strong fit—you can trust these insights.`,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-400',
      percentage: r2 * 100,
    };
  } else if (r2 > 0.70) {
    return {
      level: 'good',
      label: 'Good',
      description: `Your model explains ${(r2 * 100).toFixed(0)}% of revenue changes. This is a reliable result for decision-making.`,
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
      percentage: r2 * 100,
    };
  } else if (r2 > 0.50) {
    return {
      level: 'fair',
      label: 'Fair',
      description: `Your model explains ${(r2 * 100).toFixed(0)}% of revenue changes. Consider adding more channels or control variables to improve.`,
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-400',
      percentage: r2 * 100,
    };
  } else {
    return {
      level: 'poor',
      label: 'Needs Work',
      description: `Your model only explains ${(r2 * 100).toFixed(0)}% of revenue changes. There may be missing factors or data issues.`,
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
      percentage: Math.max(0, r2 * 100),
    };
  }
}

/**
 * Assess DECOMP.RSSD (Decomposition Root Sum of Squared Differences)
 * Measures alignment between spend patterns and attributed effects.
 * Lower is better: 0% = perfect alignment
 *
 * NOTE: The raw RSSD value is multiplied by 100 for display consistency with
 * other metrics (NRMSE, R²). The input `rssd` is the raw value (typically 0-1).
 *
 * Thresholds (applied to raw value, displayed as percentage):
 * - Excellent: < 0.10 (< 10%) - strong business alignment
 * - Good: 0.10-0.20 (10-20%) - reasonable alignment
 * - Fair: 0.20-0.40 (20-40%) - some misalignment
 * - Poor: > 0.40 (> 40%) - significant misalignment
 */
export function assessDecompRSSD(rssd: number | null | undefined): MetricQuality {
  if (rssd === undefined || rssd === null) {
    return {
      level: 'fair',
      label: 'Unknown',
      description: 'DECOMP.RSSD could not be calculated. Check channel configuration.',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-300',
      percentage: 50,
    };
  }

  // Convert to percentage for display consistency with other metrics
  const pct = rssd * 100;

  if (rssd < 0.10) {
    return {
      level: 'excellent',
      label: 'Excellent',
      description: 'Strong alignment between your spending and the effects the model detected. Results are business-sensible.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-400',
      percentage: Math.max(0, 100 - pct * 2), // 0% RSSD = 100, 50% RSSD = 0
    };
  } else if (rssd < 0.20) {
    return {
      level: 'good',
      label: 'Good',
      description: 'Reasonable alignment between spending and detected effects. Results are trustworthy.',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
      percentage: Math.max(0, 100 - pct * 2),
    };
  } else if (rssd < 0.40) {
    return {
      level: 'fair',
      label: 'Fair',
      description: 'Some channels may have unexpected effect patterns. Review the contribution breakdown for anomalies.',
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-400',
      percentage: Math.max(0, 100 - pct * 2),
    };
  } else {
    return {
      level: 'poor',
      label: 'Review Needed',
      description: 'Significant mismatch between spending and effects. Some channels may need reconfiguration.',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
      percentage: Math.max(0, 100 - pct * 2),
    };
  }
}

/**
 * Assess dataset size adequacy
 * Rule of thumb: need 10-20 observations per parameter for stable estimates
 */
export function assessDatasetSize(nObs: number, nParams: number): MetricQuality {
  const ratio = nParams > 0 ? nObs / nParams : 0;

  if (ratio >= 20) {
    return {
      level: 'excellent',
      label: 'Excellent',
      description: `${nObs} observations for ${nParams} parameters (${ratio.toFixed(0)}:1 ratio). Plenty of data for stable estimates.`,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-400',
      percentage: 100,
    };
  } else if (ratio >= 10) {
    return {
      level: 'good',
      label: 'Good',
      description: `${nObs} observations for ${nParams} parameters (${ratio.toFixed(0)}:1 ratio). Sufficient data for reliable results.`,
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
      percentage: 80,
    };
  } else if (ratio >= 5) {
    return {
      level: 'fair',
      label: 'Limited',
      description: `${nObs} observations for ${nParams} parameters (${ratio.toFixed(0)}:1 ratio). Results may be less stable. Consider more data if possible.`,
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-400',
      percentage: 50,
    };
  } else {
    return {
      level: 'poor',
      label: 'Insufficient',
      description: `Only ${nObs} observations for ${nParams} parameters (${ratio.toFixed(0)}:1 ratio). Results may be unreliable. Add more data.`,
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
      percentage: 25,
    };
  }
}

/**
 * Get an overall model quality assessment combining all metrics
 */
export function assessOverallQuality(
  nrmse: number | undefined,
  r2: number | undefined,
  decompRssd: number | null | undefined
): MetricQuality {
  const nrmseQuality = assessNRMSE(nrmse);
  const r2Quality = assessR2(r2);
  const rssdQuality = assessDecompRSSD(decompRssd);

  // Weight R² and NRMSE more heavily as they're the primary indicators
  const scores = {
    excellent: 4,
    good: 3,
    fair: 2,
    poor: 1,
  };

  const weightedScore =
    (scores[nrmseQuality.level] * 2 +
     scores[r2Quality.level] * 2 +
     scores[rssdQuality.level] * 1) / 5;

  if (weightedScore >= 3.5) {
    return {
      level: 'excellent',
      label: 'High Quality Model',
      description: 'Your model performs excellently across all metrics. You can confidently use these results for decision-making.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-400',
      percentage: 95,
    };
  } else if (weightedScore >= 2.5) {
    return {
      level: 'good',
      label: 'Good Quality Model',
      description: 'Your model performs well overall. Results are reliable for most business decisions.',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-400',
      percentage: 75,
    };
  } else if (weightedScore >= 1.5) {
    return {
      level: 'fair',
      label: 'Fair Quality Model',
      description: 'Your model works but could be improved. Consider the recommendations below to enhance accuracy.',
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-400',
      percentage: 50,
    };
  } else {
    return {
      level: 'poor',
      label: 'Model Needs Improvement',
      description: 'Your model is struggling. Review data quality, add more observations, or adjust your channel configuration.',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-400',
      percentage: 25,
    };
  }
}

/**
 * Get icon name based on quality level
 */
export function getQualityIcon(level: QualityLevel): string {
  switch (level) {
    case 'excellent':
      return 'CheckCircle';
    case 'good':
      return 'ThumbsUp';
    case 'fair':
      return 'AlertCircle';
    case 'poor':
      return 'AlertTriangle';
  }
}
